import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileCheck, Lock, Eye, EyeOff, Plus, Book, Calendar, Clock, User, MapPin, CreditCard, FileText, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Admin password - same as Booking Journal
const ADMIN_PASSWORD = 'HopeN2026!';

const notarizationTypes = [
  'Acknowledgment',
  'Jurat',
  'Copy Certification',
  'Oath/Affirmation',
  'Signature Witnessing',
  'Other'
];

const idTypes = [
  'Driver\'s License',
  'State ID',
  'Passport',
  'Military ID',
  'Other Government ID'
];

const identificationMethods = [
  'Personal Knowledge',
  'Satisfactory Evidence (ID)',
  'Credible Witness',
  'Other'
];

export default function NotaryJournal() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [activeTab, setActiveTab] = useState('entries');
  
  const [form, setForm] = useState({
    entryNumber: '',
    dateOfNotarization: '',
    timeOfNotarization: '',
    typeOfNotarization: '',
    documentTitle: '',
    documentDate: '',
    signerName: '',
    signerAddress: '',
    idTypePresented: '',
    idNumberPartial: '',
    idExpirationDate: '',
    signerWillingAware: 'Yes',
    methodOfIdentification: '',
    feeCharged: '',
    locationOfNotarization: '',
    notaryName: 'Hope N.',
    notaryCommissionNumber: '',
    commissionExpiration: '',
    notes: ''
  });

  // Check if already logged in
  useEffect(() => {
    const savedAuth = sessionStorage.getItem('notaryJournalAuth');
    if (savedAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('notaryJournalAuth', 'true');
      setLoginError('');
    } else {
      setLoginError('Incorrect password. Please try again.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('notaryJournalAuth');
  };

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const [entriesRes, nextNumRes] = await Promise.all([
        axios.get(`${API}/notary-journal`),
        axios.get(`${API}/notary-journal/next-entry-number`)
      ]);
      setEntries(entriesRes.data.reverse());
      if (!form.entryNumber) {
        setForm(prev => ({ ...prev, entryNumber: String(nextNumRes.data.nextEntryNumber) }));
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchEntries();
    }
  }, [isAuthenticated]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/notary-journal`, form);
      toast.success('Journal entry saved!', { description: `Entry #${form.entryNumber} has been recorded.` });
      
      // Reset form and get next entry number
      const nextNumRes = await axios.get(`${API}/notary-journal/next-entry-number`);
      setForm({
        entryNumber: String(nextNumRes.data.nextEntryNumber),
        dateOfNotarization: '',
        timeOfNotarization: '',
        typeOfNotarization: '',
        documentTitle: '',
        documentDate: '',
        signerName: '',
        signerAddress: '',
        idTypePresented: '',
        idNumberPartial: '',
        idExpirationDate: '',
        signerWillingAware: 'Yes',
        methodOfIdentification: '',
        feeCharged: '',
        locationOfNotarization: '',
        notaryName: form.notaryName,
        notaryCommissionNumber: form.notaryCommissionNumber,
        commissionExpiration: form.commissionExpiration,
        notes: ''
      });
      
      fetchEntries();
      setActiveTab('entries');
    } catch (error) {
      toast.error('Failed to save entry. Please try again.');
    }
    setSubmitting(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card className="shadow-2xl border-0">
            <CardHeader className="bg-gradient-to-r from-[#4f62c8] to-[#2a2873] text-white rounded-t-lg">
              <div className="flex items-center justify-center gap-3">
                <Book className="w-8 h-8" />
                <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>Notary Journal</CardTitle>
              </div>
              <p className="text-white/70 text-center text-sm mt-2">Secure access to your notary records</p>
            </CardHeader>
            <CardContent className="p-8">
              <form onSubmit={handleLogin} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#2a2873] font-medium">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter admin password"
                      className="pr-10"
                      data-testid="notary-journal-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {loginError && (
                  <p className="text-red-500 text-sm text-center">{loginError}</p>
                )}
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-[#4f62c8] to-[#2a2873] rounded-full"
                  data-testid="notary-journal-login-btn"
                >
                  <Lock className="w-4 h-4 mr-2" /> Sign In
                </Button>
              </form>
              <p className="text-center text-xs text-slate-400 mt-6">
                LifeBreath Mobile Notary Journal
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#4f62c8] to-[#2a2873] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <Book className="w-8 h-8" />
              <div>
                <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Notary Journal</h1>
                <p className="text-white/70 mt-1">Record and view all notarization entries</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Badge className="bg-white/20 text-white">{entries.length} Entries</Badge>
              <Button onClick={fetchEntries} variant="secondary" className="bg-white/10 hover:bg-white/20 text-white" disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button onClick={handleLogout} variant="secondary" className="bg-white/10 hover:bg-white/20 text-white">
                <Lock className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="entries" className="data-[state=active]:bg-[#4f62c8] data-[state=active]:text-white">
              <FileText className="w-4 h-4 mr-2" /> View Entries ({entries.length})
            </TabsTrigger>
            <TabsTrigger value="new" className="data-[state=active]:bg-[#2a2873] data-[state=active]:text-white">
              <Plus className="w-4 h-4 mr-2" /> New Entry
            </TabsTrigger>
          </TabsList>

          {/* View Entries Tab */}
          <TabsContent value="entries">
            <div className="space-y-4">
              {entries.length === 0 ? (
                <Card className="text-center py-12">
                  <CardContent>
                    <Book className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No journal entries yet</p>
                    <Button onClick={() => setActiveTab('new')} className="mt-4 bg-[#4f62c8]">
                      <Plus className="w-4 h-4 mr-2" /> Create First Entry
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                entries.map((entry, idx) => (
                  <motion.div key={entry.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}>
                    <Card className="border-l-4 border-l-[#4f62c8] hover:shadow-lg transition-shadow">
                      <CardContent className="p-0">
                        <button
                          onClick={() => setExpandedEntry(expandedEntry === entry.id ? null : entry.id)}
                          className="w-full p-6 text-left"
                        >
                          <div className="flex justify-between items-start">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge className="bg-[#4f62c8]">Entry #{entry.entryNumber}</Badge>
                                <Badge variant="outline">{entry.typeOfNotarization}</Badge>
                              </div>
                              <h3 className="text-lg font-bold text-[#2a2873]">{entry.documentTitle}</h3>
                              <p className="text-[#5a2b8a]">{entry.signerName}</p>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right text-sm text-slate-500">
                                <p className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {entry.dateOfNotarization}</p>
                                <p className="flex items-center gap-1"><Clock className="w-4 h-4" /> {entry.timeOfNotarization}</p>
                              </div>
                              {expandedEntry === entry.id ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                            </div>
                          </div>
                        </button>
                        
                        {expandedEntry === entry.id && (
                          <div className="px-6 pb-6 pt-2 border-t bg-slate-50/50">
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                              <div>
                                <p className="text-slate-500">Document Date</p>
                                <p className="font-medium text-[#2a2873]">{entry.documentDate}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Signer Address</p>
                                <p className="font-medium text-[#2a2873]">{entry.signerAddress}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Location of Notarization</p>
                                <p className="font-medium text-[#2a2873]">{entry.locationOfNotarization}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">ID Type</p>
                                <p className="font-medium text-[#2a2873]">{entry.idTypePresented}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">ID Number (Partial)</p>
                                <p className="font-medium text-[#2a2873]">{entry.idNumberPartial}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">ID Expiration</p>
                                <p className="font-medium text-[#2a2873]">{entry.idExpirationDate}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Method of Identification</p>
                                <p className="font-medium text-[#2a2873]">{entry.methodOfIdentification}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Signer Willing & Aware</p>
                                <p className="font-medium text-[#2a2873]">{entry.signerWillingAware}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Fee Charged</p>
                                <p className="font-medium text-[#2a2873]">{entry.feeCharged}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Notary Name</p>
                                <p className="font-medium text-[#2a2873]">{entry.notaryName}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Commission #</p>
                                <p className="font-medium text-[#2a2873]">{entry.notaryCommissionNumber}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Commission Expiration</p>
                                <p className="font-medium text-[#2a2873]">{entry.commissionExpiration}</p>
                              </div>
                            </div>
                            {entry.notes && (
                              <div className="mt-4 p-3 bg-white rounded-lg">
                                <p className="text-slate-500 text-sm">Notes</p>
                                <p className="text-[#2a2873]">{entry.notes}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                ))
              )}
            </div>
          </TabsContent>

          {/* New Entry Tab */}
          <TabsContent value="new">
            <Card className="border-0 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-[#4f62c8]/5 to-[#2a2873]/5">
                <CardTitle className="text-2xl text-[#2a2873] flex items-center gap-2">
                  <Plus className="w-6 h-6" /> New Journal Entry
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Entry Info */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-[#2a2873] border-b pb-2">Entry Information</h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label>Journal Entry # *</Label>
                        <Input required value={form.entryNumber} onChange={e => setForm({...form, entryNumber: e.target.value})} placeholder="1" />
                      </div>
                      <div>
                        <Label>Date of Notarization *</Label>
                        <Input type="date" required value={form.dateOfNotarization} onChange={e => setForm({...form, dateOfNotarization: e.target.value})} />
                      </div>
                      <div>
                        <Label>Time *</Label>
                        <Input type="time" required value={form.timeOfNotarization} onChange={e => setForm({...form, timeOfNotarization: e.target.value})} />
                      </div>
                    </div>
                  </div>

                  {/* Document Info */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-[#2a2873] border-b pb-2">Document Information</h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label>Type of Notarization *</Label>
                        <Select value={form.typeOfNotarization} onValueChange={v => setForm({...form, typeOfNotarization: v})}>
                          <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                          <SelectContent>
                            {notarizationTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Document Title *</Label>
                        <Input required value={form.documentTitle} onChange={e => setForm({...form, documentTitle: e.target.value})} placeholder="e.g., Power of Attorney" />
                      </div>
                      <div>
                        <Label>Document Date *</Label>
                        <Input type="date" required value={form.documentDate} onChange={e => setForm({...form, documentDate: e.target.value})} />
                      </div>
                    </div>
                  </div>

                  {/* Signer Info */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-[#2a2873] border-b pb-2">Signer Information</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Signer Name *</Label>
                        <Input required value={form.signerName} onChange={e => setForm({...form, signerName: e.target.value})} placeholder="Full legal name" />
                      </div>
                      <div>
                        <Label>Signer Address *</Label>
                        <Input required value={form.signerAddress} onChange={e => setForm({...form, signerAddress: e.target.value})} placeholder="Full address" />
                      </div>
                    </div>
                  </div>

                  {/* ID Info */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-[#2a2873] border-b pb-2">Identification</h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label>ID Type Presented *</Label>
                        <Select value={form.idTypePresented} onValueChange={v => setForm({...form, idTypePresented: v})}>
                          <SelectTrigger><SelectValue placeholder="Select ID type" /></SelectTrigger>
                          <SelectContent>
                            {idTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>ID Number (Partial) *</Label>
                        <Input required value={form.idNumberPartial} onChange={e => setForm({...form, idNumberPartial: e.target.value})} placeholder="Last 4 digits" />
                      </div>
                      <div>
                        <Label>ID Expiration Date *</Label>
                        <Input type="date" required value={form.idExpirationDate} onChange={e => setForm({...form, idExpirationDate: e.target.value})} />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Signer Willing & Aware *</Label>
                        <Select value={form.signerWillingAware} onValueChange={v => setForm({...form, signerWillingAware: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Yes">Yes</SelectItem>
                            <SelectItem value="No">No</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Method of Identification *</Label>
                        <Select value={form.methodOfIdentification} onValueChange={v => setForm({...form, methodOfIdentification: v})}>
                          <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                          <SelectContent>
                            {identificationMethods.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Transaction Info */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-[#2a2873] border-b pb-2">Transaction Details</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Fee Charged *</Label>
                        <Input required value={form.feeCharged} onChange={e => setForm({...form, feeCharged: e.target.value})} placeholder="$25.00" />
                      </div>
                      <div>
                        <Label>Location of Notarization *</Label>
                        <Input required value={form.locationOfNotarization} onChange={e => setForm({...form, locationOfNotarization: e.target.value})} placeholder="Full address" />
                      </div>
                    </div>
                  </div>

                  {/* Notary Info */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-[#2a2873] border-b pb-2">Notary Information</h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label>Notary Name *</Label>
                        <Input required value={form.notaryName} onChange={e => setForm({...form, notaryName: e.target.value})} />
                      </div>
                      <div>
                        <Label>Commission # *</Label>
                        <Input required value={form.notaryCommissionNumber} onChange={e => setForm({...form, notaryCommissionNumber: e.target.value})} />
                      </div>
                      <div>
                        <Label>Commission Expiration *</Label>
                        <Input type="date" required value={form.commissionExpiration} onChange={e => setForm({...form, commissionExpiration: e.target.value})} />
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-[#2a2873] border-b pb-2">Additional Notes</h3>
                    <Textarea
                      rows={3}
                      value={form.notes}
                      onChange={e => setForm({...form, notes: e.target.value})}
                      placeholder="Any additional notes about this notarization..."
                    />
                  </div>

                  <Button type="submit" size="lg" className="w-full bg-gradient-to-r from-[#4f62c8] to-[#2a2873] rounded-full" disabled={submitting}>
                    {submitting ? 'Saving...' : 'Save Journal Entry'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
